import React from 'react';
import './StatusBadge.css';

export type WxbStatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'error' | 'cyan';
export type WxbStatusVariant = 'soft' | 'outline' | 'dot';
export type WxbStatusSize = 'sm' | 'md';
export type WxbStatusKind =
  | 'draft'
  | 'pending'
  | 'running'
  | 'success'
  | 'warning'
  | 'error'
  | 'blocked'
  | 'disabled'
  | 'locked'
  | 'released'
  | 'unreleased';

const STATUS_META: Record<WxbStatusKind, { label: string; tone: WxbStatusTone }> = {
  draft: { label: '草稿', tone: 'neutral' },
  pending: { label: '待处理', tone: 'warning' },
  running: { label: '运行中', tone: 'info' },
  success: { label: '已完成', tone: 'success' },
  warning: { label: '需关注', tone: 'warning' },
  error: { label: '异常', tone: 'error' },
  blocked: { label: '阻断', tone: 'error' },
  disabled: { label: '停用', tone: 'neutral' },
  locked: { label: '已锁定', tone: 'neutral' },
  released: { label: '已放行', tone: 'success' },
  unreleased: { label: '未放行', tone: 'warning' },
};

export interface WxbStatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  status?: WxbStatusKind;
  label?: React.ReactNode;
  tone?: WxbStatusTone;
  variant?: WxbStatusVariant;
  size?: WxbStatusSize;
  pulse?: boolean;
}

const joinClasses = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(' ');

export const WxbStatusBadge: React.FC<WxbStatusBadgeProps> = ({
  status,
  label,
  tone,
  variant = 'soft',
  size = 'md',
  pulse = false,
  className = '',
  children,
  ...props
}) => {
  const meta = status ? STATUS_META[status] : undefined;
  const resolvedTone = tone ?? meta?.tone ?? 'neutral';
  const resolvedLabel = children ?? label ?? meta?.label;

  return (
    <span
      className={joinClasses(
        'wxb-status-badge',
        `wxb-status-badge-${resolvedTone}`,
        `wxb-status-badge-${variant}`,
        `wxb-status-badge-${size}`,
        pulse && 'wxb-status-badge-pulse',
        className,
      )}
      {...props}
    >
      <span className="wxb-status-badge-indicator" aria-hidden="true" />
      <span className="wxb-status-badge-label">{resolvedLabel}</span>
    </span>
  );
};

export type WxbSeverityLevel = 'none' | 's1' | 's2' | 's3' | 's4';

const SEVERITY_META: Record<WxbSeverityLevel, { label: string; tone: WxbStatusTone }> = {
  none: { label: '正常', tone: 'success' },
  s1: { label: 'S1 关注', tone: 'cyan' },
  s2: { label: 'S2 预警', tone: 'warning' },
  s3: { label: 'S3 严重', tone: 'error' },
  s4: { label: 'S4 阻断', tone: 'error' },
};

export interface WxbSeverityBadgeProps extends Omit<WxbStatusBadgeProps, 'status' | 'tone'> {
  level: WxbSeverityLevel;
}

export const WxbSeverityBadge: React.FC<WxbSeverityBadgeProps> = ({
  level,
  label,
  variant = 'soft',
  ...props
}) => {
  const meta = SEVERITY_META[level];
  return (
    <WxbStatusBadge
      {...props}
      label={label ?? meta.label}
      tone={meta.tone}
      variant={variant}
      className={joinClasses('wxb-severity-badge', `wxb-severity-badge-${level}`, props.className)}
    />
  );
};
